import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Button, Input, Textarea } from '../components/ui';

// #6c — the confidential retailer negotiation playbook no longer ships in the
// client bundle. It is fetched at runtime from GET /api/retailer-profiles, which
// requires a signed-in caller who is not an explicit Viewer.
// #2b — the profiles now live in commercial.RetailerProfile (typed SQL, audited);
// edits go through PUT /api/retailer-profiles/{id} (requireCommercialWrite).

const CLASS_COLOR = { 'A-Class': 'var(--success)', 'B-Class': 'var(--warn)' };

const Centered = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 160px)', minHeight: 360, padding: 24 }}>
    {children}
  </div>
);

export default function RetailerProfiles() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const { data: RETAILERS = [], isLoading, error } = useQuery({
    queryKey: ['retailer-profiles'],
    queryFn: async () => {
      const res = await apiFetch('/retailer-profiles');
      if (res.status === 403) throw new Error('restricted');
      if (!res.ok) throw new Error(`Failed to load retailer profiles (HTTP ${res.status})`);
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: (count, e) => e?.message !== 'restricted' && count < 1,
  });
  const [selected, setSelected] = useState('woolworths');
  const [editing, setEditing] = useState(null); // null | retailer object being edited
  const r = RETAILERS.find(x => x.id === selected) ?? RETAILERS[0];

  const saveMutation = useMutation({
    mutationFn: async (p) => {
      const body = JSON.stringify({
        name: p.name, class: p.class, stores: p.stores, descriptor: p.descriptor, difot: p.difot,
        commercialCase: p.commercialCase, warning: p.warning, shopperProfile: p.shopperProfile,
        talkingTrack: p.talkingTrack, stats: p.stats, keyEvidence: p.keyEvidence, asks: p.asks,
      });
      const res = await apiFetch(`/retailer-profiles/${p.id}`, { method: 'PUT', body });
      if (res.status === 403) throw new Error('You have read-only (Viewer) access — editing requires the Editor role.');
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['retailer-profiles'] }); setEditing(null); },
  });

  if (isLoading) {
    return <Centered><span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Loading retailer profiles…</span></Centered>;
  }
  if (error) {
    const restricted = error.message === 'restricted';
    return (
      <Centered>
        <div className="card" style={{ maxWidth: 460, textAlign: 'center', padding: 28 }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>{restricted ? '🔒' : '⚠'}</div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
            {restricted ? 'Restricted content' : 'Could not load profiles'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {restricted
              ? 'Retailer negotiation profiles contain confidential commercial strategy. Your access is read-only (Viewer) — contact an admin if you need access.'
              : error.message}
          </div>
        </div>
      </Centered>
    );
  }
  if (!r) {
    return <Centered><span style={{ fontSize: 14, color: 'var(--text-muted)' }}>No retailer profiles available.</span></Centered>;
  }

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 110px)', minHeight: 500 }}>
      {/* Sidebar */}
      <div style={{ width: 200, minWidth: 200, background: 'var(--bg-elevated)', borderRight: '1px solid var(--border)', overflowY: 'auto' }}>
        <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Retailers
          </div>
        </div>
        {RETAILERS.map(ret => (
          <div
            key={ret.id}
            onClick={() => setSelected(ret.id)}
            style={{
              padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
              background: selected === ret.id ? 'var(--border)' : 'transparent',
              borderLeft: `3px solid ${selected === ret.id ? 'var(--info)' : 'transparent'}`,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: selected === ret.id ? 'var(--text-primary)' : 'var(--text-body)' }}>
              {ret.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <span style={{ fontSize: 10, color: CLASS_COLOR[ret.class] || 'var(--text-muted)', fontWeight: 600 }}>{ret.class}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{ret.stores.split(' ')[0]} stores</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 32px' }}>
        {/* Header */}
        <div style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 20, fontWeight: 700 }}>{r.name}</h2>
                <span style={{ fontSize: 11, fontWeight: 700, color: CLASS_COLOR[r.class] || 'var(--text-muted)',
                  background: `color-mix(in srgb, ${CLASS_COLOR[r.class] || 'var(--text-muted)'} 14%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${CLASS_COLOR[r.class] || 'var(--text-muted)'} 30%, transparent)`,
                  borderRadius: 4, padding: '2px 7px' }}>{r.class}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {r.stores} · {r.descriptor}{r.difot ? ` · DIFOT: ${r.difot}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <Button size="sm" onClick={() => setEditing(r)}>Edit</Button>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', lineHeight: 1.6 }}>
                Majans Retailer Negotiation One-Pager<br />
                March 2026 · NielsenIQ Actuals
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* KPI Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {r.stats.map((s, i) => (
              <div key={i} className="card" style={{ textAlign: 'center', padding: '14px 12px' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Commercial Case */}
              <div className="card">
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  The Commercial Case
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.7 }}>{r.commercialCase}</p>
              </div>

              {/* Key Evidence */}
              <div className="card">
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Key Evidence
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {r.keyEvidence.map((e, i) => (
                    <li key={i} style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6 }}>{e}</li>
                  ))}
                </ul>
              </div>

              {/* Recommended Asks */}
              <div className="card">
                <div style={{ fontWeight: 700, color: 'var(--info)', fontSize: 13, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Recommended Asks
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {r.asks.map((a, i) => (
                    <li key={i} style={{ fontSize: 13, color: 'var(--info)', lineHeight: 1.6 }}>{a}</li>
                  ))}
                </ul>
              </div>

              {/* Warning */}
              <div style={{ background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, lineHeight: 1 }}>⚠</span>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--warn-text)', lineHeight: 1.6 }}>{r.warning}</p>
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Shopper Profile */}
              <div className="card">
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Who Buys at This Retailer
                </div>
                {r.shopperProfile.split('\n\n').map((para, i) => (
                  <p key={i} style={{ margin: 0, marginBottom: i < r.shopperProfile.split('\n\n').length - 1 ? 10 : 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.7 }}>{para}</p>
                ))}
              </div>

              {/* Talking Track */}
              <div style={{ background: 'var(--info-soft)', border: '1px solid #dbeafe', borderRadius: 8, padding: '16px' }}>
                <div style={{ fontWeight: 700, color: 'var(--info)', fontSize: 13, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Talking Track
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--info)', lineHeight: 1.8, fontStyle: 'italic' }}>"{r.talkingTrack}"</p>
              </div>

              {/* Source note */}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
                CONFIDENTIAL — Majans Pty Ltd · Penetration data: NielsenIQ March 2026 · Prepared by Hayley Bonaccorso
              </div>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <RetailerProfileEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(p) => saveMutation.mutate(p)}
          saving={saveMutation.isPending}
          error={saveMutation.error}
        />
      )}
    </div>
  );
}

function RetailerProfileEditor({ initial, onClose, onSave, saving, error }) {
  const [form, setForm] = useState(() => ({
    ...initial,
    stats: (initial.stats ?? []).map((s) => ({ value: s.value ?? '', label: s.label ?? '' })),
    keyEvidenceText: (initial.keyEvidence ?? []).join('\n'),
    asksText: (initial.asks ?? []).join('\n'),
  }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setStat = (i, k) => (e) => setForm((f) => {
    const stats = f.stats.map((s, j) => (j === i ? { ...s, [k]: e.target.value } : s));
    return { ...f, stats };
  });
  const addStat = () => setForm((f) => ({ ...f, stats: [...f.stats, { value: '', label: '' }] }));
  const removeStat = (i) => setForm((f) => ({ ...f, stats: f.stats.filter((_, j) => j !== i) }));

  const submit = () => {
    const lines = (t) => (t || '').split('\n').map((s) => s.trim()).filter(Boolean);
    onSave({
      id: form.id, name: form.name, class: form.class, stores: form.stores,
      descriptor: form.descriptor, difot: form.difot, commercialCase: form.commercialCase,
      warning: form.warning, shopperProfile: form.shopperProfile, talkingTrack: form.talkingTrack,
      stats: form.stats.filter((s) => s.value || s.label),
      keyEvidence: lines(form.keyEvidenceText),
      asks: lines(form.asksText),
    });
  };

  const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
  const field = (k, label, placeholder) => (
    <div><div style={lbl}>{label}</div><Input value={form[k] ?? ''} onChange={set(k)} placeholder={placeholder} /></div>
  );
  const area = (k, label, rows = 3, placeholder) => (
    <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>{label}</div><Textarea rows={rows} value={form[k] ?? ''} onChange={set(k)} placeholder={placeholder} /></div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(640px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Edit {form.name} profile</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
          {field('name', 'Name')}
          {field('class', 'Class', 'A-Class, B-Class')}
          {field('stores', 'Stores', '1,050 stores')}
          {field('difot', 'DIFOT', '98.4%')}
          {area('descriptor', 'Descriptor', 2, 'Largest grocery retailer')}
        </div>

        {/* Stats */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={lbl}>Headline Stats</div>
            <button type="button" onClick={addStat} style={{ fontSize: 11, color: 'var(--info)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add stat</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {form.stats.map((s, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 28px', gap: 8, alignItems: 'center' }}>
                <Input value={s.value} onChange={setStat(i, 'value')} placeholder="34%" />
                <Input value={s.label} onChange={setStat(i, 'label')} placeholder="Bhuja $ share" />
                <button type="button" onClick={() => removeStat(i)} title="Remove" style={{ fontSize: 14, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px 12px' }}>
          {area('commercialCase', 'The Commercial Case', 4)}
          {area('keyEvidenceText', 'Key Evidence (one per line)', 4)}
          {area('asksText', 'Recommended Asks (one per line)', 4)}
          {area('warning', 'Warning', 2)}
          {area('shopperProfile', 'Who Buys at This Retailer (blank line separates paragraphs)', 5)}
          {area('talkingTrack', 'Talking Track', 3)}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {error && <span style={{ fontSize: 12, color: 'var(--danger)', flex: 1 }}>{String(error.message ?? error)}</span>}
          <span style={{ flex: error ? 0 : 1 }} />
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={saving || !form.name} onClick={submit}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}
